import React, { useState, useRef, useEffect } from 'react';
import { lbl, inp, sectionDivider, sectionHeading } from './shared';

const INCOME_OPTIONS = [
  'SSDI', 'SSI', 'VA Benefits', 'Social Security', 'Employment',
  'Pension', 'Investments', 'Rental Income', 'Other',
];

const INCOME_RANGES = {
  single: [
    { label: 'Under $1,350/mo',        eligibility: 'QMB' },
    { label: '$1,350 – $1,616/mo',     eligibility: 'SLMB' },
    { label: '$1,617 – $1,816/mo',     eligibility: 'QI' },
    { label: '$1,817 – $2,015/mo',     eligibility: 'Extra Help / LIS' },
    { label: 'Over $2,015/mo',         eligibility: 'None' },
  ],
  married: [
    { label: 'Under $1,824/mo',        eligibility: 'QMB' },
    { label: '$1,824 – $2,184/mo',     eligibility: 'SLMB' },
    { label: '$2,185 – $2,455/mo',     eligibility: 'QI' },
    { label: '$2,456 – $2,725/mo',     eligibility: 'Extra Help / LIS' },
    { label: 'Over $2,725/mo',         eligibility: 'None' },
  ],
};

const ELIGIBILITY_DESCRIPTIONS = {
  QMB: 'Covers Part A & B premiums, deductibles, coinsurance, and copays.',
  SLMB: 'Covers your Medicare Part B premium.',
  QI: 'Covers your Medicare Part B premium (limited enrollment).',
  'Extra Help / LIS': 'Helps pay Part D drug costs — no deductible, reduced copays.',
};

const dropdownBase = {
  position: 'absolute', left: 0, right: 0,
  background: '#fff', border: '1.5px solid #c5d5e8', borderRadius: 8,
  zIndex: 200, maxHeight: 220, overflowY: 'auto',
  boxShadow: '0 6px 16px rgba(26,51,100,0.13)',
};

function SingleSelect({ options, value, onChange, placeholder }) {
  const [open, setOpen] = useState(false);
  const [openUp, setOpenUp] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    function handleClick(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  function handleToggle() {
    if (!open && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      setOpenUp(window.innerHeight - rect.bottom < 240);
    }
    setOpen(o => !o);
  }

  const listStyle = {
    ...dropdownBase,
    ...(openUp ? { bottom: '100%', marginBottom: 2 } : { top: '100%', marginTop: 2 }),
  };

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={handleToggle}
        style={{
          ...inp, textAlign: 'left', cursor: 'pointer',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          color: value ? '#1a3364' : '#aaa',
        }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
          {value || placeholder}
        </span>
        <span style={{ marginLeft: 8, fontSize: 12, flexShrink: 0 }}>{open ? '▲' : '▼'}</span>
      </button>
      {open && (
        <div style={listStyle}>
          {options.map(opt => (
            <div
              key={opt.label}
              onMouseDown={() => { onChange(opt.label); setOpen(false); }}
              style={{
                padding: '10px 14px', cursor: 'pointer', fontSize: 14, color: '#1a3364',
                borderBottom: '1px solid #f4f8fc',
                background: value === opt.label ? '#f0f6ff' : '#fff',
              }}
              onMouseEnter={e => { if (value !== opt.label) e.currentTarget.style.background = '#dbeef8'; }}
              onMouseLeave={e => { e.currentTarget.style.background = value === opt.label ? '#f0f6ff' : '#fff'; }}
            >
              {opt.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const dropdownStyle = { ...dropdownBase, top: '100%', marginTop: 2 };

function YesNo({ question, value, onChange }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      background: '#f4f8fc', borderRadius: 10, padding: '13px 16px', marginBottom: 10,
    }}>
      <span style={{ fontSize: 14, color: '#1a3364', flex: 1, paddingRight: 16, lineHeight: 1.4 }}>
        {question}
      </span>
      <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
        {['Yes', 'No'].map(opt => {
          const isSelected = value === (opt === 'Yes');
          return (
            <button key={opt} type="button" onClick={() => onChange(opt === 'Yes')} style={{
              padding: '8px 20px', borderRadius: 8,
              border: '2px solid #1a3364',
              background: isSelected ? '#1a3364' : '#fff',
              color: isSelected ? '#fff' : '#1a3364',
              fontWeight: 600, fontSize: 14, cursor: 'pointer',
            }}>
              {opt}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function MultiSelect({ options, selected, onChange, placeholder }) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);
  const selectedValues = normalizeSelected(selected);

  useEffect(() => {
    function handleClick(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  function toggle(opt) {
    onChange(selectedValues.includes(opt) ? selectedValues.filter(s => s !== opt) : [...selectedValues, opt]);
  }

  const display = selectedValues.length === 0 ? placeholder : selectedValues.join(', ');

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        style={{
          ...inp,
          textAlign: 'left',
          cursor: 'pointer',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          color: selectedValues.length === 0 ? '#aaa' : '#1a3364',
        }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
          {display}
        </span>
        <span style={{ marginLeft: 8, fontSize: 12, flexShrink: 0 }}>{open ? '▲' : '▼'}</span>
      </button>
      {open && (
        <div style={dropdownStyle}>
          {options.map(opt => (
            <div
              key={opt}
              onMouseDown={() => toggle(opt)}
              style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '10px 14px', cursor: 'pointer', fontSize: 14,
                borderBottom: '1px solid #f4f8fc',
                background: selectedValues.includes(opt) ? '#f0f6ff' : '#fff',
              }}
              onMouseEnter={e => { if (!selectedValues.includes(opt)) e.currentTarget.style.background = '#dbeef8'; }}
              onMouseLeave={e => { e.currentTarget.style.background = selectedValues.includes(opt) ? '#f0f6ff' : '#fff'; }}
            >
              <div style={{
                width: 18, height: 18, borderRadius: 4, flexShrink: 0,
                border: `2px solid ${selectedValues.includes(opt) ? '#1a3364' : '#c5d5e8'}`,
                background: selectedValues.includes(opt) ? '#1a3364' : '#fff',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                {selectedValues.includes(opt) && <span style={{ color: '#fff', fontSize: 11, fontWeight: 700 }}>✓</span>}
              </div>
              <span style={{ color: '#1a3364' }}>{opt}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function normalizeSelected(value) {
  if (Array.isArray(value)) return value;
  if (!value) return [];
  const raw = String(value).trim();
  if (raw.startsWith('[') && raw.endsWith(']')) {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return parsed;
    } catch {
      return raw
        .slice(1, -1)
        .split(',')
        .map(item => item.trim().replace(/^["']|["']$/g, ''))
        .filter(Boolean);
    }
  }
  return raw.split(',').map(item => item.trim()).filter(Boolean);
}

export default function BenefitsIncome({ data, onChange }) {
  const ranges = data.isMarried === null ? [] : INCOME_RANGES[data.isMarried ? 'married' : 'single'];
  const selectedRange = ranges.find(r => r.label === data.totalMonthlyIncome) || null;
  const eligibility = selectedRange ? selectedRange.eligibility : null;

  function handleRangeChange(label) {
    const range = ranges.find(r => r.label === label);
    onChange('totalMonthlyIncome', label);
    onChange('estimatedEligibility', range ? range.eligibility : '');
  }

  return (
    <div>
      {/* Current coverage */}
      <div style={{ marginBottom: 4 }}>
        <div style={sectionHeading}>Current Coverage</div>
        <YesNo
          question="Are you a veteran?"
          value={data.isVeteran}
          onChange={v => onChange('isVeteran', v)}
        />
        <YesNo
          question="Do you receive prescriptions from the VA?"
          value={data.vaRx}
          onChange={v => onChange('vaRx', v)}
        />
        <YesNo
          question="Do you currently have Part D, Medicare Supplement, HMO, PPO, or PFFS plan?"
          value={data.hasCurrentPlan}
          onChange={v => onChange('hasCurrentPlan', v)}
        />
        <YesNo
          question="Do you currently have private insurance?"
          value={data.hasPrivateInsurance}
          onChange={v => onChange('hasPrivateInsurance', v)}
        />
        <YesNo
          question="Are you a tobacco user?"
          value={data.isSmoker}
          onChange={v => onChange('isSmoker', v)}
        />
      </div>

      {/* Income */}
      <div style={sectionDivider}>
        <div style={sectionHeading}>Income Information</div>
        <p style={{ fontSize: 13, color: '#4a6288', marginTop: 0, marginBottom: 16, lineHeight: 1.6 }}>
          This information is optional. It helps us identify plans and programs you may qualify for,
          but you are not required to answer anything you are not comfortable sharing.
        </p>

        <YesNo
          question="Are you married?"
          value={data.isMarried}
          onChange={v => {
            onChange('isMarried', v);
            onChange('totalMonthlyIncome', '');
            onChange('estimatedEligibility', '');
          }}
        />

        <div style={{ marginBottom: 14 }}>
          <label style={lbl}>Source of Income</label>
          <MultiSelect
            options={INCOME_OPTIONS}
            selected={data.incomeSource || []}
            onChange={val => onChange('incomeSource', val)}
            placeholder="Select all that apply…"
          />
        </div>

        {(data.incomeSource || []).includes('Other') && (
          <div style={{ marginBottom: 14 }}>
            <label style={lbl}>Other Income Source</label>
            <input style={inp} value={data.otherIncome} onChange={e => onChange('otherIncome', e.target.value)} />
          </div>
        )}

        {data.isMarried !== null && (
          <div>
            <label style={lbl}>Total Combined Household Income (Monthly)</label>
            <SingleSelect
              options={ranges}
              value={data.totalMonthlyIncome || ''}
              onChange={handleRangeChange}
              placeholder="Select a range…"
            />

            {eligibility !== null && (
              <div style={{
                marginTop: 10,
                background: eligibility === 'None' ? '#f4f8fc' : '#edf7ed',
                border: `1.5px solid ${eligibility === 'None' ? '#c5d5e8' : '#66bb6a'}`,
                borderRadius: 10, padding: '12px 16px',
              }}>
                {eligibility === 'None' ? (
                  <div style={{ fontSize: 13, color: '#4a6288' }}>
                    No Medicare assistance programs identified based on this income.
                  </div>
                ) : (
                  <>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#2e7d32', marginBottom: 4 }}>
                      May qualify for: {eligibility}
                    </div>
                    <div style={{ fontSize: 12, color: '#388e3c' }}>
                      {ELIGIBILITY_DESCRIPTIONS[eligibility]}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
